import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";
import { DesignInterface } from "@/types/design";

export interface DesignParams {
  evseID: string;
}

const fetchDesign = async (params: DesignParams, signal?: AbortSignal): Promise<DesignInterface> => {
  const response = await axios.get(`/api/evse/${params.evseID}/get-design/`, { signal });
  return response.data as DesignInterface;
};

type QueryKeyType = readonly ["design", "details", DesignParams | "disabled"];

export const designKeys = {
  all: ["design"] as const,
  details: () => [...designKeys.all, "details"] as const,
  detail: (params: DesignParams | "disabled") => [...designKeys.details(), params] as const
};

export const useDesign = (
  params: DesignParams | null,
  options?: Omit<UseQueryOptions<DesignInterface, Error, DesignInterface, QueryKeyType>, "queryKey" | "queryFn">
) => {
  const isEnabled = params !== null && !!params.evseID;

  return useQuery({
    queryKey: params ? designKeys.detail(params) : designKeys.detail("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("design params are required");
      }
      return fetchDesign(params, signal);
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: isEnabled,
    ...options
  });
};
